import * as tslib_1 from "tslib";
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { isFieldValueFilled } from '../../hooks';
import { FloatingLabel } from '../basic';
import { LocalizeButton as Button } from '../';
import _ from 'lodash';
export const TextboxInput = (_a) => {
    var { type, value, label, placeholder, error, onfocus, onblur, onchange, autoFocus, allowUnmask, preventPaste, prefix, controlledLabel, onLabelStateChange, disabled, defaultLabelId, labelPrefix, children, step } = _a, rest = tslib_1.__rest(_a, ["type", "value", "label", "placeholder", "error", "onfocus", "onblur", "onchange", "autoFocus", "allowUnmask", "preventPaste", "prefix", "controlledLabel", "onLabelStateChange", "disabled", "defaultLabelId", "labelPrefix", "children", "step"]);
    const { name } = rest, extra = tslib_1.__rest(rest, ["name"]);
    const trimOnBlurArray = [
        'address1',
        'address2',
        'city',
        'state',
        'postalCode',
        'email',
        'Name',
        'name',
        'description',
        'acctSuffix',
        'answer',
        'employer',
        'employmentFunctions',
        'jobPosition',
        'disclosuresStakeholderTickerSymbol',
        'disclosuresGovOfficialImmediateFamily',
        'disclosuresGovOfficialPoliticalOrg',
        'disclosuresForeignBankUSAgent',
        'securityResponse',
    ];
    const checkTrimOnBlurArray = () => {
        return trimOnBlurArray.findIndex(n => {
            return (defaultLabelId && _.includes(defaultLabelId, n)) || _.includes(name, n);
        });
    };
    const isTrimOnBlur = checkTrimOnBlurArray() !== -1 ? true : false;
    const isPhoneNumber = _.includes(name, 'phoneNumber');
    const formatPhoneNumber = (value) => {
        if (!value)
            return value;
        const phoneNumber = value.replace(/[^\d]/g, '');
        if (phoneNumber.length < 4)
            return phoneNumber;
        if (phoneNumber.length < 7)
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
        if (phoneNumber.length <= 10)
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
        if (phoneNumber.length === 11)
            return `${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1, 4)}-${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7)}`;
        else
            return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2, 6)}-${phoneNumber.slice(6)}`;
    };
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState();
    const val = inputValue || value;
    const [maskPassword, setMaskPassword] = useState(true);
    const [floatingLabelState, setFloatingLabelState] = useState({
        isFilled: isFieldValueFilled(val),
    });
    const updateFloatingLabelState = useCallback((state) => {
        setFloatingLabelState(state);
        if (onLabelStateChange) {
            onLabelStateChange(state);
        }
    }, [onLabelStateChange]);
    const onFocus = useCallback((e) => {
        const input = e ? e.currentTarget : inputRef.current;
        if (!input) {
            return;
        }
        if (onfocus && e) {
            onfocus(e);
        }
        /* Keep isFilled based on value so placeholder clears only when user types, not on focus */
        updateFloatingLabelState({
            isFilled: isFieldValueFilled(input.value),
            hasFocus: true,
        });
    }, [onfocus, updateFloatingLabelState]);
    const onBlur = useCallback((e) => {
        if (isTrimOnBlur) {
            e.target.value = e.target.value.trim();
            onChange(e);
        }
        if (onblur) {
            onblur(e);
        }
        updateFloatingLabelState({
            isFilled: isFieldValueFilled(e.target.value),
            hasFocus: false,
        });
    }, [onblur, updateFloatingLabelState]);
    const onChange = useCallback((e) => {
        const rawValue = e.target.value;
        const val = type === 'number' ? parseFloat(rawValue) : rawValue;
        if (onchange) {
            onchange(e, val);
        }
        else {
            setInputValue(rawValue);
        }
        updateFloatingLabelState({
            isFilled: isFieldValueFilled(val),
            hasFocus: floatingLabelState.hasFocus,
        });
    }, [type, onchange, updateFloatingLabelState, floatingLabelState.hasFocus]);
    const onPaste = useCallback((e) => {
        if (preventPaste) {
            e.preventDefault();
        }
    }, [preventPaste]);
    const onDragDrop = useCallback((e) => {
        if (preventPaste) {
            e.preventDefault();
        }
    }, [preventPaste]);
    useEffect(() => {
        if (autoFocus) {
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [autoFocus]);
    useEffect(() => {
        if (!controlledLabel) {
            updateFloatingLabelState({
                isFilled: isFieldValueFilled(val),
                hasFocus: floatingLabelState.hasFocus,
            });
        }
    }, [val, controlledLabel, floatingLabelState.hasFocus, updateFloatingLabelState]);
    const icon = useMemo(() => {
        if (type === 'password' && allowUnmask) {
            return (React.createElement(Button, { type: maskPassword ? 'show' : 'hide', onClick: () => setMaskPassword(!maskPassword), tabIndex: -1 },
                React.createElement("i", { className: `fal fa-lg ${maskPassword ? 'fa-eye' : 'fa-eye-slash'}` })));
        }
        return undefined;
    }, [type, maskPassword, allowUnmask]);
    const handleIncrement = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || !inputRef.current)
            return;
        // Get current value - prefer the controlled value prop, then fallback to input value
        const currentValueStr = String(val || inputRef.current.value || '');
        const currentValue = parseFloat(currentValueStr) || 0;
        const stepValue = step ? parseFloat(step.toString()) : 1;
        const min = extra.min ? parseFloat(extra.min.toString()) : undefined;
        const max = extra.max ? parseFloat(extra.max.toString()) : undefined;
        const newValue = currentValue + stepValue;
        let finalValue = newValue;
        if (min !== undefined && finalValue < min)
            finalValue = min;
        if (max !== undefined && finalValue > max)
            finalValue = max;
        // Create the new value string
        const newValueString = finalValue.toString();
        // Update the input DOM element's value
        if (inputRef.current) {
            const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
            if (valueDescriptor && valueDescriptor.set) {
                valueDescriptor.set.call(inputRef.current, newValueString);
            }
            else {
                inputRef.current.value = newValueString;
            }
            // Create and dispatch a proper input event
            const inputEvent = new Event('input', { bubbles: true });
            inputRef.current.dispatchEvent(inputEvent);
            // Also create a change event for React/Formik
            const changeEvent = new Event('change', { bubbles: true });
            inputRef.current.dispatchEvent(changeEvent);
            inputRef.current.focus();
        }
    }, [disabled, step, extra.min, extra.max, val]);
    const handleDecrement = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || !inputRef.current)
            return;
        // Get current value - prefer the controlled value prop, then fallback to input value
        const currentValueStr = String(val || inputRef.current.value || '');
        const currentValue = parseFloat(currentValueStr) || 0;
        const stepValue = step ? parseFloat(step.toString()) : 1;
        const min = extra.min ? parseFloat(extra.min.toString()) : undefined;
        const max = extra.max ? parseFloat(extra.max.toString()) : undefined;
        const newValue = currentValue - stepValue;
        let finalValue = newValue;
        if (min !== undefined && finalValue < min)
            finalValue = min;
        if (max !== undefined && finalValue > max)
            finalValue = max;
        // Create the new value string
        const newValueString = finalValue.toString();
        // Update the input DOM element's value
        if (inputRef.current) {
            const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
            if (valueDescriptor && valueDescriptor.set) {
                valueDescriptor.set.call(inputRef.current, newValueString);
            }
            else {
                inputRef.current.value = newValueString;
            }
            // Create and dispatch a proper input event
            const inputEvent = new Event('input', { bubbles: true });
            inputRef.current.dispatchEvent(inputEvent);
            // Also create a change event for React/Formik
            const changeEvent = new Event('change', { bubbles: true });
            inputRef.current.dispatchEvent(changeEvent);
            inputRef.current.focus();
        }
    }, [disabled, step, extra.min, extra.max, val]);
    const numberButtons = useMemo(() => {
        if (type === 'number') {
            return (React.createElement("div", { className: "flabel-number-buttons" },
                React.createElement("button", { type: "button", className: "flabel-number-button flabel-number-button-up", onClick: handleIncrement, disabled: disabled, tabIndex: -1, "aria-label": "Increase value" },
                    React.createElement("i", { className: "fal fa-chevron-up" })),
                React.createElement("button", { type: "button", className: "flabel-number-button flabel-number-button-down", onClick: handleDecrement, disabled: disabled, tabIndex: -1, "aria-label": "Decrease value" },
                    React.createElement("i", { className: "fal fa-chevron-down" }))));
        }
        return undefined;
    }, [type, disabled, handleIncrement, handleDecrement]);
    const fieldType = useMemo(() => {
        if (type === 'number') {
            return type;
        }
        if (!maskPassword) {
            return 'text';
        }
        return type;
    }, [maskPassword, type]);
    return (React.createElement(React.Fragment, null,
        React.createElement(FloatingLabel, { id: name, className: error ? 'invalid' : '', label: placeholder || label || name, prefix: prefix, icon: icon, state: floatingLabelState, disabled: disabled },
            React.createElement("input", Object.assign({ ref: inputRef, className: "flabel-input", id: name, type: fieldType, value: isFieldValueFilled(val) ? (isPhoneNumber ? formatPhoneNumber(val) : val) : '', disabled: disabled, onFocus: onFocus, onBlur: onBlur, onChange: onChange, onPaste: onPaste, onDrag: onDragDrop, onDrop: onDragDrop, autoComplete: name, step: step }, extra)),
            numberButtons)));
};
//# sourceMappingURL=Textbox.js.map